import React, { useState, useEffect } from 'react'
import { api } from '../api'
import ExportButton from '../components/ExportButton'
import LastUpdated from '../components/LastUpdated'

const statusEmoji = { 'blocked': '🔴', 'active': '🟡', 'at-risk': '🟡', 'in-progress': '🔵', 'resolved': '✅' }
const statusLabels = { 'blocked': 'Blocked', 'active': 'Active', 'at-risk': 'At Risk', 'in-progress': 'In Progress', 'resolved': 'Resolved' }

export default function ExecutiveTracker() {
  const [issues, setIssues] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [newIssue, setNewIssue] = useState({ title: '', priority: 'P0', status: 'active', description: '', owner: '', dueDate: '' })

  useEffect(() => {
    api.getExecutiveTracker().then(setIssues).catch(console.error).finally(() => setLoading(false))
  }, [])

  const toggleActionItem = async (issueId, idx) => {
    const issue = issues.find(i => i.id === issueId)
    if (!issue) return
    const actionItems = [...issue.actionItems]
    actionItems[idx] = { ...actionItems[idx], done: !actionItems[idx].done }
    try {
      await api.updateExecutiveTracker(issueId, { actionItems })
      setIssues(prev => prev.map(i => i.id === issueId ? { ...i, actionItems } : i))
    } catch (e) { console.error(e) }
  }

  const resolveIssue = async (issueId) => {
    try {
      await api.updateExecutiveTracker(issueId, { status: 'resolved' })
      setIssues(prev => prev.map(i => i.id === issueId ? { ...i, status: 'resolved' } : i))
    } catch (e) { console.error(e) }
  }

  const createIssue = async () => {
    try {
      const created = await api.createExecutiveTracker({ ...newIssue, actionItems: [] })
      setIssues(prev => [...prev, created])
      setShowCreate(false)
      setNewIssue({ title: '', priority: 'P0', status: 'active', description: '', owner: '', dueDate: '' })
    } catch (e) { console.error(e) }
  }

  if (loading) return <div className="loading">Loading executive tracker...</div>

  const active = issues.filter(i => i.status !== 'resolved')
  const resolved = issues.filter(i => i.status === 'resolved')

  return (
    <div className="exec-tracker-page">
      <div className="page-header">
        <h2>Executive Tracker</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LastUpdated data={issues} />
          <ExportButton data={issues} filename="executive-tracker" />
          <button className="btn-primary" onClick={() => setShowCreate(!showCreate)}>+ New Issue</button>
        </div>
      </div>

      {showCreate && (
        <div className="create-form">
          <input placeholder="Title" value={newIssue.title} onChange={e => setNewIssue(n => ({ ...n, title: e.target.value }))} />
          <select value={newIssue.priority} onChange={e => setNewIssue(n => ({ ...n, priority: e.target.value }))}>
            <option value="P0">P0</option><option value="P1">P1</option><option value="P2">P2</option>
          </select>
          <select value={newIssue.status} onChange={e => setNewIssue(n => ({ ...n, status: e.target.value }))}>
            <option value="active">Active</option><option value="blocked">Blocked</option><option value="at-risk">At Risk</option>
          </select>
          <input placeholder="Owner" value={newIssue.owner} onChange={e => setNewIssue(n => ({ ...n, owner: e.target.value }))} />
          <input placeholder="Due date" value={newIssue.dueDate} onChange={e => setNewIssue(n => ({ ...n, dueDate: e.target.value }))} />
          <textarea placeholder="Description" value={newIssue.description} onChange={e => setNewIssue(n => ({ ...n, description: e.target.value }))} />
          <div className="form-actions">
            <button className="btn-primary" onClick={createIssue}>Create</button>
            <button className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
          </div>
        </div>
      )}

      <h3>Active Issues ({active.length})</h3>
      {active.map(issue => (
        <IssueCard key={issue.id} issue={issue} onToggle={toggleActionItem} onResolve={resolveIssue} />
      ))}

      {resolved.length > 0 && <>
        <h3 className="resolved-header">Resolved ({resolved.length})</h3>
        {resolved.map(issue => (
          <IssueCard key={issue.id} issue={issue} onToggle={toggleActionItem} />
        ))}
      </>}
    </div>
  )
}

function IssueCard({ issue, onToggle, onResolve }) {
  return (
    <div className={`issue-card ${issue.status}`}>
      <div className="issue-header">
        <span className="issue-title">
          {statusEmoji[issue.status] || '📌'} {issue.title}
        </span>
        <div className="issue-badges">
          <span className={`badge priority-${issue.priority?.toLowerCase()}`}>{issue.priority}</span>
          <span className={`badge status-${issue.status}`}>{statusLabels[issue.status] || issue.status}</span>
        </div>
      </div>
      <div className="issue-meta">
        <span>Owner: {issue.owner}</span>
        {issue.dueDate && <span>Due: {issue.dueDate}</span>}
      </div>
      {issue.description && <p className="issue-desc">{issue.description}</p>}
      {issue.actionItems?.length > 0 && (
        <div className="action-items">
          {issue.actionItems.map((ai, idx) => (
            <label key={idx} className={`action-item ${ai.done ? 'done' : ''}`}>
              <input type="checkbox" checked={ai.done} onChange={() => onToggle(issue.id, idx)} />
              <span>{ai.text}</span>
            </label>
          ))}
        </div>
      )}
      {issue.status !== 'resolved' && onResolve && (
        <div className="issue-actions">
          <button className="btn-sm" onClick={() => onResolve(issue.id)}>✅ Resolve</button>
        </div>
      )}
    </div>
  )
}
